<script setup>
  import { onBeforeMount, ref, computed } from "vue";
  import { useRoute } from "vue-router";
  import router from "@/routes/vueRouter.js";
  import PageHeader from "@/components/PageElements/PageHeader.vue";
  import PageSection from "@/components/Atoms/PageSection.vue";
  import { useClientStore } from "@/stores/ClientStore";
  import { storeToRefs } from "pinia";
  import { CheckInScheduleService } from "@/services/CheckInScheduleService";

  const route = useRoute();
  const clientStore = useClientStore();
  const { currentClient } = storeToRefs(clientStore);
  const breadCrumbs = ref(null);

  // Editable schedule fields only; identity (first/last name, email) is managed
  // by Jumpstart - a trainer-edits-client-identity endpoint does not exist.
  const scheduleForm = ref({ checkInDayOfWeek: null });

  onBeforeMount(async () => {
    await clientStore.fetchAndSetCurrentClient(route.params.id);
    if (currentClient.value) {
      scheduleForm.value = {
        checkInDayOfWeek: currentClient.value.checkInDayOfWeek,
      };
      breadCrumbs.value = [
        { label: "My Clients", to: { name: "ActiveClientsPage" } },
        {
          label: currentClient.value.fullName,
          to: { name: "ViewClientPage", params: { id: currentClient.value.id } },
        },
        {
          label: "Edit",
          to: { name: "EditClientPage", params: { id: currentClient.value.id } },
        },
      ];
    }
  });

  const submitHandler = async () => {
    await CheckInScheduleService.update(currentClient.value.participantId, {
      check_in_day_of_week: scheduleForm.value.checkInDayOfWeek,
    });
    router.push({ name: "ViewClientPage", params: { id: currentClient.value.id } });
  };

  const checkInDayOptions = computed(() => [
    { value: "1", label: "Monday" },
    { value: "2", label: "Tuesday" },
    { value: "3", label: "Wednesday" },
    { value: "4", label: "Thursday" },
    { value: "5", label: "Friday" },
    { value: "6", label: "Saturday" },
    { value: "7", label: "Sunday" },
  ]);
</script>

<template>
  <div>
    <PageHeader
      v-if="currentClient"
      :bread-crumbs="breadCrumbs"
      :page-title="`Edit ${currentClient.fullName}`"
    />

    <div v-if="currentClient">
      <!-- Identity fields are read-only; identity is managed by Jumpstart.
           A trainer-edits-client-identity endpoint deliberately does not exist. -->
      <PageSection section-title="Profile">
        <div class="space-y-2 text-sm text-gray-700">
          <div><span class="font-medium">First Name:</span> {{ currentClient.firstName }}</div>
          <div><span class="font-medium">Last Name:</span> {{ currentClient.lastName }}</div>
          <div><span class="font-medium">Email:</span> {{ currentClient.email }}</div>
        </div>
      </PageSection>

      <FormKit
        v-model="scheduleForm"
        type="form"
        @submit="submitHandler"
      >
        <PageSection section-title="Check-In Schedule">
          <FormKit
            type="select"
            name="checkInDayOfWeek"
            label="Check-In Day"
            :options="checkInDayOptions"
          />
        </PageSection>
      </FormKit>
    </div>
  </div>
</template>
